/**
 * ISATS Portal — Applicant profile schema normalization (read side).
 *
 * The `applications` collection has accumulated several generations of field
 * shapes. The one-time migration script (`scripts/normalize-application-schema.cjs`)
 * collapses legacy fields in Firestore; this module is the defensive read-side
 * counterpart that guarantees components always receive canonical field names
 * regardless of what is stored, so the UI never has to carry `A || B || C`
 * fallbacks forever.
 *
 * Only non-PII fields are renamed (educationalBackground, workExperience,
 * programTerm / assignment / programPreferences). Encrypted PII blocks
 * that reach the client still encrypted (e.g. the server-side decrypt
 * passthrough when keys are unavailable) are stripped to undefined by
 * sanitizeEncryptedPiiBlocks so no display surface can render an
 * {iv, ciphertext, authTag} object as a React child (error #31).
 */

import type { Application } from '../types';
import { sanitizeEncryptedPiiBlocks } from './piiDisplay';

const CANONICAL_EDUCATION_KEYS: Array<[string, string]> = [
  ['institutionName', 'institution'],
  ['institutionName', 'school'],
  ['degreeEarned', 'degree'],
  ['degreeEarned', 'qualification'],
  ['fieldOfStudy', 'field'],
];

const CANONICAL_WORK_KEYS: Array<[string, string]> = [
  ['employerName', 'employer'],
  ['jobTitle', 'role'],
  ['employerAddress', 'location'],
];

function normalizeEducationEntry(edu: any): any {
  if (!edu || typeof edu !== 'object') return edu;
  let changed = false;
  const out: any = { ...edu };
  for (const [canonical, legacy] of CANONICAL_EDUCATION_KEYS) {
    if (!out[canonical] && out[legacy]) {
      out[canonical] = out[legacy];
      changed = true;
    }
  }
  return changed ? out : edu;
}

function normalizeWorkEntry(work: any): any {
  if (!work || typeof work !== 'object') return work;
  let changed = false;
  const out: any = { ...work };
  for (const [canonical, legacy] of CANONICAL_WORK_KEYS) {
    if (!out[canonical] && out[legacy]) {
      out[canonical] = out[legacy];
      changed = true;
    }
  }
  return changed ? out : work;
}

/**
 * Returns a copy of the application with canonical education/work keys and a
 * single consistent program term across root, assignment, and preferences.
 * Returns the SAME reference when nothing needed changing.
 */
export function normalizeApplicationForDisplay(app: Application | null | undefined): Application | null | undefined {
  if (!app) return app;
  // Strip any still-encrypted PII blocks before display normalization:
  // an envelope that survives to a component crashes React (#31) or
  // throws on string operations. Sanitized fields become undefined and
  // every surface falls back to its normal empty state.
  const sanitized = sanitizeEncryptedPiiBlocks(app);
  const anyApp = sanitized as any;
  let changed = sanitized !== app;
  const out: any = { ...anyApp };

  if (Array.isArray(anyApp.educationalBackground)) {
    const list = anyApp.educationalBackground.map(normalizeEducationEntry);
    if (list.some((e: any, i: number) => e !== anyApp.educationalBackground[i])) {
      out.educationalBackground = list;
      changed = true;
    }
  }

  if (Array.isArray(anyApp.workExperience)) {
    const list = anyApp.workExperience.map(normalizeWorkEntry);
    if (list.some((w: any, i: number) => w !== anyApp.workExperience[i])) {
      out.workExperience = list;
      changed = true;
    }
  }

  // Term: pick one effective value and mirror it across all three aliases so
  // reads are consistent (BatchCenter/EmailTab/sponsor views read assignment
  // first; admin/sponsor detail read root first).
  const effectiveTerm =
    anyApp.programTerm ||
    anyApp.assignment?.programTerm ||
    anyApp.programPreferences?.programTerm ||
    '';
  if (effectiveTerm) {
    if (out.programTerm !== effectiveTerm) {
      out.programTerm = effectiveTerm;
      changed = true;
    }
    if (out.assignment && out.assignment.programTerm !== effectiveTerm) {
      out.assignment = { ...out.assignment, programTerm: effectiveTerm };
      changed = true;
    }
    if (out.programPreferences && out.programPreferences.programTerm !== effectiveTerm) {
      out.programPreferences = { ...out.programPreferences, programTerm: effectiveTerm };
      changed = true;
    }
  }

  return changed ? (out as Application) : app;
}

/**
 * Coerces any legacy date value (ISO string, Date, or Firestore Timestamp)
 * into a `YYYY-MM-DD` string safe for `<input type="date">`. Returns '' for
 * null/undefined/invalid values so React never renders a non-string into a
 * date input (which can behave unpredictably across browsers).
 */
export function toDateInputValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' && value.trim()) {
    // Accept "2024-01-15", "2024-01-15T10:00:00Z", "Jan 15, 2024" etc.
    const s = value.trim();
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;
    const d = new Date(s);
    return !isNaN(d.getTime()) ? d.toISOString().slice(0, 10) : '';
  }
  if (value instanceof Date) {
    return !isNaN(value.getTime()) ? value.toISOString().slice(0, 10) : '';
  }
  if (typeof value === 'object') {
    const v = value as any;
    if (typeof v.toDate === 'function') {
      const d = v.toDate();
      if (d instanceof Date && !isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    }
    if (typeof v.seconds === 'number') {
      const d = new Date(v.seconds * 1000);
      if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    }
  }
  return '';
}

/**
 * Returns a copy of a `passportImmigration` record whose date fields are
 * guaranteed to be `YYYY-MM-DD` strings (Timestamp/Date → ISO). Guards the
 * passport edit flows against legacy stored shapes.
 */
export function normalizePassportDates(passport: any): any {
  if (!passport || typeof passport !== 'object') return passport;
  return {
    ...passport,
    issueDate: toDateInputValue(passport.issueDate),
    expirationDate: toDateInputValue(passport.expirationDate),
    intendedArrival: toDateInputValue(passport.intendedArrival),
  };
}

// Validation helpers
export const isValidPhone = (p: string) => /^\+?[0-9\s\-()]{7,20}$/.test((p || '').trim());
export const isValidEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || '').trim());
export const isFutureDate = (d: string) => {
  if (!d) return false;
  const date = new Date(d);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return date > now;
};

export interface MissingField {
  label: string;
  fieldId?: string;
  reason?: string;
}

export interface StepValidationResult {
  isValid: boolean;
  missingFields: MissingField[];
}

/**
 * Evaluates required fields for a given step in the 9-step applicant creation flow.
 * Returns an itemized list of missing/invalid required fields for non-punishing modal checkpoints.
 */
export function validateStepFields(step: number, data: any): StepValidationResult {
  const missing: MissingField[] = [];

  switch (step) {
    case 1: {
      const p = data?.personal || {};
      if (!p.firstName?.trim()) missing.push({ label: 'First Name', fieldId: 'field_firstName' });
      if (!p.lastName?.trim()) missing.push({ label: 'Last Name', fieldId: 'field_lastName' });
      if (!p.fullNameLegal?.trim()) missing.push({ label: 'Full Legal Name (as on Passport)', fieldId: 'field_fullNameLegal' });
      if (!p.dob) missing.push({ label: 'Date of Birth', fieldId: 'field_dob' });
      if (!p.countryOfBirth) missing.push({ label: 'Country of Birth', fieldId: 'field_countryOfBirth' });
      if (!p.countryOfCitizenship) missing.push({ label: 'Country of Citizenship', fieldId: 'field_countryOfCitizenship' });
      if (!p.gender) missing.push({ label: 'Gender', fieldId: 'field_gender' });
      if (!p.phone?.trim()) {
        missing.push({ label: 'Phone Number', fieldId: 'field_phone' });
      } else if (!isValidPhone(p.phone)) {
        missing.push({ label: 'Phone Number (Invalid format, e.g. +1-876-555-0199)', fieldId: 'field_phone' });
      }
      if (Array.isArray(p.immediateFamilyOverseas)) {
        p.immediateFamilyOverseas.forEach((fam: any, idx: number) => {
          if (!fam.name?.trim() || !fam.relationship) {
            missing.push({ label: `Overseas Family Member #${idx + 1} Name & Relationship`, fieldId: `field_family_${idx}` });
          }
        });
      }
      break;
    }

    case 2: {
      const pass = data?.passport || {};
      if (!pass.passportNumber?.trim()) missing.push({ label: 'Passport Number', fieldId: 'field_passportNumber' });
      if (!pass.passportCountry) missing.push({ label: 'Passport Issuing Country', fieldId: 'field_passportCountry' });
      if (!pass.issueDate) {
        missing.push({ label: 'Passport Issue Date', fieldId: 'field_issueDate' });
      } else if (isFutureDate(pass.issueDate)) {
        missing.push({ label: 'Passport Issue Date (Cannot be in the future)', fieldId: 'field_issueDate' });
      }
      if (!pass.expirationDate) missing.push({ label: 'Passport Expiration Date', fieldId: 'field_expirationDate' });
      break;
    }

    case 3: {
      // Education list check
      const list = data?.educationList || [];
      if (list.length === 0) {
        missing.push({ label: 'At least one Education record', fieldId: 'section_education' });
      }
      break;
    }

    case 4: {
      // Work list check (Work is optional in some programs, but if required or staged)
      break;
    }

    case 5: {
      const contacts = data?.contacts || [];
      if (contacts.length === 0) {
        missing.push({ label: 'At least one Emergency Contact', fieldId: 'section_contacts' });
      } else {
        contacts.forEach((c: any, idx: number) => {
          if (!c.fullName?.trim()) missing.push({ label: `Emergency Contact #${idx + 1} Full Name`, fieldId: `field_contact_name_${idx}` });
          if (!c.relationship) missing.push({ label: `Emergency Contact #${idx + 1} Relationship`, fieldId: `field_contact_rel_${idx}` });
          if (!c.phone?.trim()) {
            missing.push({ label: `Emergency Contact #${idx + 1} Phone Number`, fieldId: `field_contact_phone_${idx}` });
          } else if (!isValidPhone(c.phone)) {
            missing.push({ label: `Emergency Contact #${idx + 1} Valid Phone Number`, fieldId: `field_contact_phone_${idx}` });
          }
          if (c.email && !isValidEmail(c.email)) {
            missing.push({ label: `Emergency Contact #${idx + 1} Valid Email Address`, fieldId: `field_contact_email_${idx}` });
          }
        });
      }
      break;
    }

    case 6: {
      const pref = data?.preferences || {};
      const programType = data?.programType || '';
      if (!pref.preferredIndustry?.trim()) missing.push({ label: 'Preferred Industry', fieldId: 'field_preferredIndustry' });
      if (!/canada|uk\b|united\s*kingdom|australia|ireland|new\s*zealand|germany|france|spain|japan|south\s*korea|singapore/i.test(programType)) {
        if (!pref.preferredState) missing.push({ label: 'Preferred State / Region', fieldId: 'field_preferredState' });
      }
      if (!pref.programTerm && !data?.activeApp?.programTerm) missing.push({ label: 'Program Term / Intake Year', fieldId: 'field_programTerm' });
      break;
    }

    case 7: {
      // Document uploads check
      break;
    }

    case 8: {
      // Review step
      break;
    }

    case 9: {
      const activeApp = data?.activeApp;
      if (!activeApp?.declarationSigned) {
        missing.push({ label: 'Signature & Declaration Confirmation', fieldId: 'section_declaration' });
      }
      break;
    }
  }

  return {
    isValid: missing.length === 0,
    missingFields: missing,
  };
}
